import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  GlobalStyles,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import logo from '../assets/divergent-logo.jpg';
import { useAuth } from '../context/AuthContext';
import RoleSelectionDialog from '../components/common/RoleSelectionDialog';

const Login = () => {
  const { login, microsoftLogin, showRoleSelection, setShowRoleSelection, pendingUser, selectRole } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
  });

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'remember' ? checked : value,
    }));
  };

  // Function to determine redirect path based on user status
  const getRedirectPath = (user) => {
    // Admin/IT Support users go to admin dashboard
    if (user && (user.primaryRole === 'admin' || user.primaryRole === 'itsupport')) {
      return '/admin/dashboard';
    }
    
    // Default redirect to existing employee
    return '/existing';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await login(formData.email, formData.password);
      
      if (result.requiresRoleSelection) {
        // Role selection dialog will be shown automatically
        return;
      }
      
      toast.success('Login successful!');
      console.log('Login response:', result);
      
      // Use the new redirect logic based on user status
      const redirectPath = getRedirectPath(result.user);
      navigate(redirectPath);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed. Please try again.');
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const result = await microsoftLogin();
      
      if (result.requiresRoleSelection) {
        // Role selection dialog will be shown automatically
        return;
      }
      
      toast.success("Login successful!");
      console.log("Login response:", result);

      // Navigate based on role
      navigateToRoleBasedPage(result.user);

    } catch (error) {
      console.error("Login error:", error);
      
      // Show user-friendly error message
      const errorMessage = error.message || "Login failed. Please try again.";
      toast.error(errorMessage);
      
      // If it's a hash empty error, suggest refreshing the page
      if (error.message && error.message.includes('hash_empty_error')) {
        toast.info("Please refresh the page and try signing in again.");
      }
    }
  };

  const handleRoleSelect = async (selectedRole) => {
    try {
      // Map the simplified role selection to actual roles
      let actualRole = selectedRole;
      
      // If user has itsupport role and selects admin, use itsupport
      if (selectedRole === 'admin' && pendingUser?.roles?.includes('itsupport')) {
        actualRole = 'itsupport';
      }
      
      const result = await selectRole(actualRole);
      
      toast.success(`Login successful as ${actualRole}!`);
      console.log('Role selection response:', result);

      // Navigate based on selected role
      navigateToRoleBasedPage(result.user);

    } catch (error) {
      console.error('Role selection error:', error);
      toast.error(error.message || 'Role selection failed. Please try again.');
    }
  };

  const navigateToRoleBasedPage = (user) => {
    const redirectPath = getRedirectPath(user);
    navigate(redirectPath);
  };

  const handleCloseRoleSelection = () => {
    setShowRoleSelection(false);
    // Optionally redirect to login or show a message
    toast.info('Please select a role to continue');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#7e57c2',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: isMobile ? 2 : 0,
      }}
    >
      <GlobalStyles styles={{ html: { backgroundColor: '#7e57c2' }, body: { backgroundColor: '#7e57c2' } }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <img
          src={logo}
          alt="Company Logo"
          style={{
            maxWidth: isMobile ? 400 : 400,
            width: '100%',
            marginBottom: 16,
          }}
        />

        <Card sx={{ width: '100%', maxWidth: 400, mx: 'auto', boxShadow: 3 }}>
          <CardContent sx={{ p: isMobile ? 3 : 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography sx={{ fontWeight: 600, fontSize: isMobile ? 20 : 25, mb: 2 }}>
                 Dive-In Quonnect
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>

              <Button
                fullWidth
                variant="outlined"
                onClick={handleMicrosoftLogin}
                startIcon={
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg"
                    alt="Microsoft"
                    style={{ height: '20px' }}
                  />
                }
              >
                Sign in with Microsoft
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  or continue with
                </Typography>
              </Divider>

            {/* <form> */}
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="remember"
                      checked={formData.remember}
                      onChange={handleChange}
                    />
                  }
                  label="Remember me"
                />
                <Button color="primary" size="small">
                  Forgot Password?
                </Button>
              </Box>

              <Button
                fullWidth
                variant="contained"
                type="submit"
                size="large"
                sx={{ mb: 2 }}
              >
                Sign In
              </Button>

              
            </form>
          </CardContent>
        </Card>
      </Box>

      {/* Role Selection Dialog */}
      <RoleSelectionDialog
        open={showRoleSelection}
        onClose={handleCloseRoleSelection}
        onRoleSelect={handleRoleSelect}
        user={pendingUser}
        roles={pendingUser?.roles || []}
      />
    </Box>
  );
};

export default Login;
